/**
 * Random access to the bytes of a media file, local or remote.
 *
 * The audio readers only ever need small pieces of a file (the container's
 * index, then the audio samples), so a remote file is read with HTTP range
 * requests and a local one with Blob.slice. Neither downloads the video.
 */

export interface ByteSource {
  /** Total length in bytes. */
  readonly size: number;
  /** Exactly `length` bytes from `offset` (fewer only at the end of the file). */
  read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array>;
}

export class ByteSourceError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'ByteSourceError';
  }
}

const abortError = () => {
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
};

export const isAbortError = (error: unknown): boolean =>
  Boolean(error) && (error as { name?: string }).name === 'AbortError';

const blobToArrayBuffer = (blob: Blob): Promise<ArrayBuffer> =>
  typeof blob.arrayBuffer === 'function'
    ? blob.arrayBuffer()
    : new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      });

/** A local file (or any Blob). */
export const blobByteSource = (blob: Blob): ByteSource => ({
  size: blob.size,
  async read(offset, length, signal) {
    if (signal?.aborted) throw abortError();
    const start = Math.max(0, Math.min(blob.size, offset));
    const end = Math.max(start, Math.min(blob.size, offset + length));
    return new Uint8Array(await blobToArrayBuffer(blob.slice(start, end)));
  },
});

/** In-memory bytes, for tests and already-downloaded files. */
export const bufferByteSource = (bytes: Uint8Array): ByteSource => ({
  size: bytes.byteLength,
  async read(offset, length, signal) {
    if (signal?.aborted) throw abortError();
    const start = Math.max(0, Math.min(bytes.byteLength, offset));
    const end = Math.max(start, Math.min(bytes.byteLength, offset + length));
    return bytes.slice(start, end);
  },
});

interface UrlSourceOptions {
  signal?: AbortSignal;
  /** Attempts per read for network failures and 5xx answers. */
  attempts?: number;
  fetchImpl?: typeof fetch;
}

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/** Total size from a Content-Range header ("bytes 0-0/12345"). */
const sizeFromContentRange = (value: string | null): number | null => {
  const match = value ? /\/(\d+)\s*$/.exec(value) : null;
  return match ? Number(match[1]) : null;
};

/**
 * A remote file read with Range requests. The size comes from a one-byte
 * range request (Content-Range), or from a HEAD request when the server
 * doesn't expose that header to scripts.
 *
 * A server that ignores Range and answers 200 with the whole file is refused:
 * reading a multi-gigabyte video to get at its sound is exactly what this
 * exists to avoid.
 */
export const urlByteSource = async (url: string, options: UrlSourceOptions = {}): Promise<ByteSource> => {
  const fetchImpl = options.fetchImpl ?? fetch;
  const attempts = Math.max(1, options.attempts ?? 3);

  const fetchRange = async (start: number, endInclusive: number, signal?: AbortSignal): Promise<Response> => {
    let lastError: unknown = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (signal?.aborted) throw abortError();
      try {
        const response = await fetchImpl(url, {
          headers: { Range: `bytes=${start}-${endInclusive}` },
          signal,
          cache: 'no-store',
          credentials: 'omit',
        });
        if (response.status === 206) return response;
        if (response.status === 200) {
          // Abandon the body: it is the whole file.
          try {
            await response.body?.cancel();
          } catch {
            /* nothing to release */
          }
          throw new ByteSourceError('The media server does not support partial downloads.', 200);
        }
        if (response.status === 416) throw new ByteSourceError('The requested part of the file is out of range.', 416);
        if (response.status >= 400 && response.status < 500) {
          throw new ByteSourceError(
            response.status === 403 || response.status === 401
              ? 'The link to this video has expired or is not allowed here.'
              : `The video could not be read (HTTP ${response.status}).`,
            response.status,
          );
        }
        lastError = new ByteSourceError(`The video could not be read (HTTP ${response.status}).`, response.status);
      } catch (error) {
        if (isAbortError(error) || error instanceof ByteSourceError) throw error;
        lastError = error;
      }
      await wait(250 * 2 ** attempt, signal);
    }
    throw lastError instanceof Error ? lastError : new ByteSourceError('The video could not be read.');
  };

  const probe = await fetchRange(0, 0, options.signal);
  let size = sizeFromContentRange(probe.headers.get('content-range'));
  try {
    await probe.arrayBuffer();
  } catch {
    /* a one-byte body */
  }
  if (size === null) {
    const head = await fetchImpl(url, { method: 'HEAD', signal: options.signal, cache: 'no-store', credentials: 'omit' });
    const length = Number(head.headers.get('content-length'));
    size = head.ok && Number.isFinite(length) && length > 0 ? length : null;
  }
  if (size === null) throw new ByteSourceError('The size of the video could not be read.');
  const total = size;

  return {
    size: total,
    async read(offset, length, signal) {
      const start = Math.max(0, Math.min(total, offset));
      const end = Math.max(start, Math.min(total, offset + length));
      if (end === start) return new Uint8Array(0);
      const response = await fetchRange(start, end - 1, signal);
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (bytes.byteLength !== end - start) {
        throw new ByteSourceError('The video server returned an unexpected amount of data.');
      }
      return bytes;
    },
  };
};
